/**
 * useObjectTypeSelection Hook
 * Handles object type selection - selects all objects of a specific type on the map
 *
 * This hook:
 * - Validates type selection (requires image icon, not color-only types)
 * - Toggles selection (clicking same type again deselects)
 * - Finds all elements matching the selected type
 * - Updates selection state with matching element IDs
 * - Clears highlights when deselecting
 */

import { dia } from '@joint/core'
import { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useZoneDrawingStore } from '../../../map-editor-shared/store/zoneDrawingStore'
import {
  isZoneObjectType,
  ZONE_OBJECT_TYPE_NAME,
  ZONE_OBJECT_TYPE_LEGACY_NAMES,
} from '../../../map-editor-shared/api/zoneApi'

interface UseObjectTypeSelectionProps {
  graph: dia.Graph | null
  paper: dia.Paper | null
  selectedTypeId: string | null
  setSelectedObjectType: (type: ObjectType | null) => void
  setSelectedTypeId: (id: string | null) => void
  setSelectedElementId: (id: string | null) => void
  setSelectedElementIds: (ids: Set<string>) => void
  /** 현재 프로젝트 ID — zone drawing 시작에 필요 */
  projectId?: number | null
}

/** 로컬 ObjectType이 zone 인지 확인 (apiResp 형이 아닌 store 형으로 매칭). */
function isLocalZoneType(type: ObjectType): boolean {
  return type.name === ZONE_OBJECT_TYPE_NAME || ZONE_OBJECT_TYPE_LEGACY_NAMES.includes(type.name)
}

export const useObjectTypeSelection = ({
  graph,
  paper,
  selectedTypeId,
  setSelectedObjectType,
  setSelectedTypeId,
  setSelectedElementId,
  setSelectedElementIds,
  projectId,
}: UseObjectTypeSelectionProps) => {
  // Handle object type selection - select all objects of this type on the map
  const handleTypeSelect = (type: ObjectType | null) => {
    // Zone ObjectType ('구역' 또는 레거시 'Zone'/'존') 클릭 시 → drawing 모드 토글.
    // 일반 selection/placement 흐름과 별개. selectedObjectType 을 켜면 useObjectCreation 가
    // 클릭 위치에 default 사각형(보라색)을 placement 로 생성해버리므로 명시적으로 null 로 설정.
    if (type && isLocalZoneType(type)) {
      setSelectedObjectType(null)
      // 다른 type selection 도 정리 (zone 진입 시 기존 hover/select 깔끔하게)
      setSelectedElementId(null)
      setSelectedElementIds(new Set())

      const drawStore = useZoneDrawingStore.getState()
      // 동일 zone 타입 다시 누르면 취소
      if (drawStore.mode === 'drawing' && drawStore.objectTypeId === Number(type.id)) {
        drawStore.cancel()
        setSelectedTypeId(null)
        return
      }
      const currentFloor = useFloorStore.getState().currentFloor
      if (!currentFloor) {
        // eslint-disable-next-line no-alert -- toast infra 없음
        window.alert('Zone 을 그릴 층(floor) 이 선택되어 있지 않습니다.')
        return
      }
      if (projectId == null) {
        // eslint-disable-next-line no-alert
        window.alert('프로젝트 ID 가 없어 Zone 드로잉을 시작할 수 없습니다.')
        return
      }
      drawStore.start({
        objectTypeId: Number(type.id),
        projectId,
        floorId: currentFloor,
      })
      setSelectedTypeId(type.id)
      return
    }

    // 일반 (non-zone) path
    setSelectedObjectType(type)

    if (!graph || !paper || !type) {
      // Clear selection if no type selected
      setSelectedTypeId(null)
      setSelectedElementId(null)
      setSelectedElementIds(new Set())
      // Clear all highlights
      graph?.getCells().forEach(cell => {
        if (cell.isElement()) {
          const view = paper?.findViewByModel(cell)
          view?.unhighlight()
        }
      })
      return
    }

    // Toggle: If same type clicked again, deselect
    if (selectedTypeId === type.id) {
      setSelectedTypeId(null)
      setSelectedElementIds(new Set())
      setSelectedElementId(null)
      return
    }

    // Find all elements with this object type
    const cells = graph.getCells()
    const matchingElementIds = new Set<string>()

    cells.forEach(cell => {
      if (!cell.isElement()) return
      const element = cell as dia.Element

      const elementTypeId = element.prop('objectTypeId')
      if (elementTypeId === type.id) {
        matchingElementIds.add(element.id.toString())
      }
    })

    // Update selection state
    if (matchingElementIds.size > 0) {
      setSelectedTypeId(type.id)
      setSelectedElementIds(matchingElementIds)
      setSelectedElementId(Array.from(matchingElementIds)[0])
    }
  }

  return { handleTypeSelect }
}
